import { useEffect, useLayoutEffect, useRef, useState } from 'react'

const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

/** Keep distant recommendation code out of the first render. The server layout
 * seed reserves its space, so arrival while visible no longer requires a click. */
export const useRecommendationReveal = (enabled: boolean, ready: boolean) => {
  const anchorRef = useRef<HTMLDivElement>(null)
  const [revealed, setRevealed] = useState(false)

  useBrowserLayoutEffect(() => {
    if (!enabled || !ready || revealed) return
    const viewport = window.visualViewport
    const revealIfNearViewport = () => {
      const anchor = anchorRef.current
      if (!anchor) return
      const height = viewport ? viewport.height : window.innerHeight
      const bottom = (viewport?.offsetTop || 0) + height
      const top = anchor.getBoundingClientRect().top
      if (Number.isFinite(top) && height > 0 && top <= bottom + height) {
        setRevealed(true)
      }
    }
    revealIfNearViewport()
    // Also react when content above the anchor changes height without a scroll.
    // The geometry check remains authoritative, including the visual viewport.
    const observer = typeof IntersectionObserver === 'undefined' ? undefined : new IntersectionObserver(
      revealIfNearViewport,
      { rootMargin: `0px 0px ${Math.max(0, viewport?.height || window.innerHeight)}px 0px` },
    )
    if (anchorRef.current) observer?.observe(anchorRef.current)
    window.addEventListener('scroll', revealIfNearViewport, { passive: true })
    window.addEventListener('resize', revealIfNearViewport)
    viewport?.addEventListener('resize', revealIfNearViewport)
    viewport?.addEventListener('scroll', revealIfNearViewport)
    return () => {
      observer?.disconnect()
      window.removeEventListener('scroll', revealIfNearViewport)
      window.removeEventListener('resize', revealIfNearViewport)
      viewport?.removeEventListener('resize', revealIfNearViewport)
      viewport?.removeEventListener('scroll', revealIfNearViewport)
    }
  }, [enabled, ready, revealed])

  return { anchorRef, showRecommendations: !enabled || revealed }
}
